'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight, Loader2 } from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';
import { fetchLiveHampers, normalizeImageUrl } from '@/services/api';
import type { OccasionPackage } from '@/data/mockData';

interface OccasionItem {
  id: string;
  slug: string;
  title: string;
  category: string;
  tag: string;
  startingPrice: number;
  subtitle: string;
  description: string;
  image: string;
  minOrderQty: number;
}

// Visible window offsets from center (-4 and +4 are off-screen fade buffers)
const VISIBLE_OFFSETS = [-4, -3, -2, -1, 0, 1, 2, 3, 4];

// Exact calculation of horizontal positions ensuring uniform, tight gap between card borders
function getCardX(diff: number, cardWidth: number, gap: number = 14): number {
  const w0 = cardWidth * 1.15;
  const w1 = cardWidth * 0.86;
  const w2 = cardWidth * 0.66;
  const w3 = cardWidth * 0.48;

  const d1 = w0 / 2 + w1 / 2 + gap;
  const d2 = d1 + w1 / 2 + w2 / 2 + gap;
  const d3 = d2 + w2 / 2 + w3 / 2 + gap;
  const d4 = d3 + w3 / 2 + w3 / 2 + gap;

  if (diff === 0) return 0;
  if (diff === 1) return d1;
  if (diff === -1) return -d1;
  if (diff === 2) return d2;
  if (diff === -2) return -d2;
  if (diff === 3) return d3;
  if (diff === -3) return -d3;
  if (diff === 4) return d4;
  if (diff === -4) return -d4;
  if (diff > 4) return d4 + (diff - 4) * (w3 + gap);
  return -d4 + (diff + 4) * (w3 + gap);
}

export default function OccasionsSection() {
  const { openRfqWithData, hampers: contextHampers } = useCommerce();
  const [serverHampers, setServerHampers] = useState<OccasionPackage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [virtualIndex, setVirtualIndex] = useState(0);
  const [sizes, setSizes] = useState({ cardWidth: 285, gap: 16 });

  // Direct live fetch from the occasional hampers server database
  const fetchOccasionHampers = useCallback(async () => {
    try {
      const data = await fetchLiveHampers('occasions');
      if (Array.isArray(data) && data.length > 0) {
        setServerHampers(data);
      }
    } catch (err) {
      console.warn('[OccasionsSection] Could not load occasional hampers from server:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOccasionHampers();

    // Auto-sync whenever user refocuses tab or visibility changes
    const handleSync = () => {
      fetchOccasionHampers();
    };
    window.addEventListener('focus', handleSync);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchOccasionHampers();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Periodic 15-second background sync to stay up to date with database changes
    const interval = setInterval(fetchOccasionHampers, 15000);

    return () => {
      window.removeEventListener('focus', handleSync);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, [fetchOccasionHampers]);

  useEffect(() => {
    const updateSizes = () => {
      const w = window.innerWidth;
      if (w < 640) {
        setSizes({ cardWidth: 190, gap: 10 });
      } else if (w < 1024) {
        setSizes({ cardWidth: 235, gap: 14 });
      } else if (w < 1440) {
        setSizes({ cardWidth: 285, gap: 16 });
      } else {
        setSizes({ cardWidth: 325, gap: 20 });
      }
    };
    updateSizes();
    window.addEventListener('resize', updateSizes);
    return () => window.removeEventListener('resize', updateSizes);
  }, []);

  // Compute live occasions: prioritize live database hampers, fallback to commerce context
  const liveOccasions: OccasionItem[] = useMemo(() => {
    let source: OccasionPackage[] = [];
    if (serverHampers.length > 0) {
      source = serverHampers;
    } else if (contextHampers && contextHampers.length > 0) {
      const filtered = contextHampers.filter(
        (h) => h.moduleType === 'occasions' || ['weddings', 'housewarming', 'temple', 'birthday', 'return-gifts', 'corporate', 'festivals', 'custom'].includes(h.occasionCategory)
      );
      source = filtered.length > 0 ? filtered : contextHampers;
    }

    if (!source || source.length === 0) return [];

    return source.map((h) => ({
      id: h.id || h.slug || String((h as any)._id),
      slug: h.slug || h.id || String((h as any)._id),
      title: h.title,
      category: h.occasionCategory || h.occasion || 'Celebration',
      tag: h.badge || 'Occasion Gift',
      startingPrice: Number(h.basePrice || 350),
      subtitle: h.minOrderQty ? `MOQ: ${h.minOrderQty} Packs` : (h.tamilTitle || 'Curated Hamper'),
      description: h.description || h.subtitle || 'Sanctified confections and traditional sweets for auspicious celebrations.',
      image: normalizeImageUrl(h.image || '/images/royal_gift_box.jpg'),
      minOrderQty: Number(h.minOrderQty || 15),
    }));
  }, [serverHampers, contextHampers]);

  // Handle loading skeleton when no hampers are cached yet
  if (liveOccasions.length === 0) {
    if (isLoading) {
      return (
        <section
          id="occasions"
          className="py-16 sm:py-24 bg-white border-b border-neutral-100 select-none scroll-mt-16 overflow-hidden w-full"
        >
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 space-y-3 px-4">
            <span className="inline-block text-[11px] sm:text-xs font-bold tracking-[0.2em] text-[#950E09] uppercase">
              Curated Gift Hampers for Every Milestone
            </span>
            <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-[#0F382A] tracking-tight leading-[1.18]">
              Across Celebrations, <br />
              Traditions Are Rising.
            </h2>
          </div>
          <div className="flex items-center justify-center min-h-[440px]">
            <div className="flex flex-col items-center space-y-3">
              <Loader2 className="w-8 h-8 text-[#0F382A] animate-spin" />
              <p className="text-xs font-medium text-neutral-500">
                Fetching occasion hampers from database...
              </p>
            </div>
          </div>
        </section>
      );
    }
    return null;
  }

  const totalOccasions = liveOccasions.length;
  const activeOccasionIndex = ((virtualIndex % totalOccasions) + totalOccasions) % totalOccasions;
  const activeOccasion = liveOccasions[activeOccasionIndex];

  // Infinite seamless forward continuation
  const handleNext = () => {
    setVirtualIndex((prev) => prev + 1);
  };

  // Infinite seamless backward continuation
  const handlePrev = () => {
    setVirtualIndex((prev) => prev - 1);
  };

  return (
    <section
      id="occasions"
      className="py-16 sm:py-24 bg-white border-b border-neutral-100 select-none scroll-mt-16 overflow-hidden w-full"
    >
      {/* 1. Top Centered Headline */}
      <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 space-y-3 px-4">
        <span className="inline-block text-[11px] sm:text-xs font-bold tracking-[0.2em] text-[#950E09] uppercase">
          Curated Gift Hampers for Every Milestone
        </span>
        <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-[#0F382A] tracking-tight leading-[1.18]">
          Across Celebrations, <br />
          Traditions Are Rising.
        </h2>
      </div>

      {/* 2. Full-Width Perspective Track with Endless Seamless Forward Gliding */}
      <div className="relative w-full overflow-hidden py-6 sm:py-10 flex items-center justify-center min-h-[440px] sm:min-h-[500px] md:min-h-[560px] lg:min-h-[600px]">
        
        {/* Subtle Edge Vignettes for Elegant Panoramic Fading */}
        <div className="absolute inset-y-0 left-0 w-8 sm:w-16 md:w-24 bg-gradient-to-r from-white via-white/80 to-transparent z-30 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-8 sm:w-16 md:w-24 bg-gradient-to-l from-white via-white/80 to-transparent z-30 pointer-events-none" />

        {/* Floating Left Arrow Button */}
        <button
          type="button"
          onClick={handlePrev}
          aria-label="Previous occasion"
          className="absolute left-3 sm:left-6 md:left-8 lg:left-12 z-40 w-11 h-11 sm:w-13 sm:h-13 md:w-14 md:h-14 rounded-full bg-white/95 backdrop-blur-md shadow-2xl border border-neutral-200/80 flex items-center justify-center text-neutral-800 hover:text-black hover:scale-110 active:scale-95 transition-all cursor-pointer"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 stroke-[2.5]" />
        </button>

        {/* Floating Right Arrow Button */}
        <button
          type="button"
          onClick={handleNext}
          aria-label="Next occasion"
          className="absolute right-3 sm:right-6 md:right-10 lg:right-12 z-40 w-11 h-11 sm:w-13 sm:h-13 md:w-14 md:h-14 rounded-full bg-white/95 backdrop-blur-md shadow-2xl border border-neutral-200/80 flex items-center justify-center text-neutral-800 hover:text-black hover:scale-110 active:scale-95 transition-all cursor-pointer"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 stroke-[2.5]" />
        </button>

        {/* Endless Perspective Stage: Cards continue infinitely forward without ever reversing */}
        <div className="relative w-full h-[400px] sm:h-[480px] md:h-[530px] lg:h-[570px] flex items-center justify-center overflow-hidden">
          {VISIBLE_OFFSETS.map((diff) => {
            const cardVirtualIndex = virtualIndex + diff;
            const occasionIdx = ((cardVirtualIndex % totalOccasions) + totalOccasions) % totalOccasions;
            const item = liveOccasions[occasionIdx];
            const absDiff = Math.abs(diff);

            // Calculate precise X coordinate with uniform tight inter-card gap
            const targetX = getCardX(diff, sizes.cardWidth, sizes.gap);

            // Dynamic perspective scaling & elevation
            let scale = 1.15;
            let zIndex = 30;
            let opacity = 1;
            let shadow = 'shadow-2xl';

            if (absDiff === 0) {
              scale = 1.15;
              zIndex = 30;
              opacity = 1;
              shadow = 'shadow-2xl';
            } else if (absDiff === 1) {
              scale = 0.86;
              zIndex = 20;
              opacity = 0.88;
              shadow = 'shadow-md';
            } else if (absDiff === 2) {
              scale = 0.66;
              zIndex = 10;
              opacity = 0.62;
              shadow = 'shadow-sm';
            } else if (absDiff === 3) {
              scale = 0.48;
              zIndex = 5;
              opacity = 0.38;
              shadow = 'shadow-xs';
            } else {
              // diff === 4 or -4: off-screen buffer for buttery smooth entrance / exit
              scale = 0.35;
              zIndex = 0;
              opacity = 0;
              shadow = '';
            }

            return (
              <motion.div
                key={cardVirtualIndex}
                onClick={() => {
                  if (absDiff === 0) {
                    openRfqWithData({
                      event: item.category,
                      boxType: item.title,
                      minQty: item.minOrderQty || 25,
                      hamperId: item.id,
                      hamperSlug: item.slug,
                      price: item.startingPrice,
                    });
                  } else {
                    setVirtualIndex(cardVirtualIndex);
                  }
                }}
                animate={{
                  x: targetX,
                  scale,
                  opacity,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 200,
                  damping: 25,
                  mass: 0.9,
                }}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: `${sizes.cardWidth}px`,
                  height: `${sizes.cardWidth * 1.38}px`,
                  marginTop: `-${(sizes.cardWidth * 1.38) / 2}px`,
                  marginLeft: `-${sizes.cardWidth / 2}px`,
                  zIndex,
                  pointerEvents: absDiff > 3 ? 'none' : 'auto',
                  willChange: 'transform, opacity',
                }}
                className={`group rounded-2xl md:rounded-3xl overflow-hidden cursor-pointer bg-neutral-100 border border-neutral-200/70 transition-shadow duration-300 ${shadow}`}
              >
                {/* Photo */}
                <Image
                  src={item.image || '/images/royal_gift_box.jpg'}
                  alt={item.title || 'Elavarasi Occasion Hamper'}
                  fill
                  sizes="(max-width: 640px) 240px, (max-width: 1024px) 320px, 450px"
                  unoptimized
                  className="object-cover w-full h-full transform transition-transform duration-700 ease-out group-hover:scale-105"
                />

                {/* Darkening overlay for non-active cards */}
                {absDiff !== 0 && (
                  <div className="absolute inset-0 bg-black/10 transition-opacity group-hover:bg-transparent" />
                )}

                {/* Active Center Card (absDiff === 0): Description overlay */}
                {absDiff === 0 && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent pt-16 pb-5 px-4 sm:px-5 text-left pointer-events-auto">
                    <div className="flex items-center justify-between gap-1.5 mb-1.5">
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold tracking-wider uppercase bg-[#950E09]/90 text-amber-200 border border-amber-300/30 backdrop-blur-xs">
                        {item.subtitle || item.category}
                      </span>
                      <span className="text-[11px] sm:text-xs font-bold text-amber-300">
                        From ₹{item.startingPrice}
                        <span className="text-[10px] font-normal text-white/70 ml-0.5">/box</span>
                      </span>
                    </div>

                    <h3 className="font-display font-bold text-base sm:text-lg md:text-xl text-white leading-tight drop-shadow-sm line-clamp-1 mb-1">
                      {item.title}
                    </h3>

                    <p className="text-xs sm:text-[13px] text-neutral-100 font-sans leading-relaxed line-clamp-3">
                      {item.description}
                    </p>

                    <div className="mt-3 flex items-center justify-between pt-2.5 border-t border-white/20 text-xs font-semibold text-amber-300 group-hover:text-amber-200 transition-colors">
                      <span>Customize This Hamper</span>
                      <span className="w-5 h-5 rounded-full bg-white/20 group-hover:bg-white/30 text-white flex items-center justify-center transition-all">
                        <ArrowRight className="w-3.5 h-3.5 stroke-[2.5]" />
                      </span>
                    </div>
                  </div>
                )}

                {/* Direct Neighbor Cards (absDiff === 1): Title & Brief snippet */}
                {absDiff === 1 && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/55 to-transparent pt-12 pb-3.5 px-3.5 text-left transition-opacity duration-300">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-amber-300/90 truncate mb-0.5">
                      {item.category}
                    </span>
                    <h4 className="text-xs sm:text-sm font-bold text-white leading-tight line-clamp-1">
                      {item.title}
                    </h4>
                    <p className="text-[10px] sm:text-[11px] text-neutral-300 line-clamp-2 mt-0.5 font-sans leading-snug">
                      {item.description}
                    </p>
                  </div>
                )}

                {/* Distant Cards (absDiff >= 2): Clean Title Badge */}
                {absDiff >= 2 && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent pt-8 pb-2.5 px-2 text-center">
                    <span className="block text-[10px] sm:text-xs font-bold text-white/90 truncate">
                      {item.title}
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

      </div>

      {/* 3. The Directional Tracker (FROM TRADITION ——————→ TO CELEBRATION) */}
      <div className="flex items-center justify-center space-x-3 text-[10px] sm:text-xs font-bold tracking-[0.25em] text-neutral-800 uppercase mt-8 sm:mt-10 select-none px-4">
        <span>FROM TRADITION</span>
        <div className="flex items-center">
          <div className="w-14 sm:w-24 md:w-32 h-[1.5px] bg-neutral-800" />
          <ChevronRight className="w-3.5 h-3.5 -ml-1 text-neutral-800 stroke-[3]" />
        </div>
        <span>TO CELEBRATION</span>
      </div>

      {/* 4. Bottom Description Paragraph with Smooth Crossfade */}
      <div className="max-w-2xl mx-auto text-center mt-5 sm:mt-6 px-4 space-y-2">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeOccasion.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="space-y-2"
          >
            <p className="text-xs sm:text-sm md:text-[15px] text-neutral-700 font-sans leading-relaxed">
              {activeOccasion.description}
            </p>
            <div className="text-[11px] sm:text-xs font-bold text-[#950E09] uppercase tracking-wider pt-1">
              {activeOccasion.title} • Starting from ₹{activeOccasion.startingPrice} / box
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* 5. Bottom Action Buttons */}
      <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 px-4">
        <button
          type="button"
          onClick={() =>
            openRfqWithData({
              event: activeOccasion.category,
              boxType: activeOccasion.title,
              minQty: activeOccasion.minOrderQty || 25,
              hamperId: activeOccasion.id,
              hamperSlug: activeOccasion.slug,
              price: activeOccasion.startingPrice,
            })
          }
          className="group inline-flex items-center space-x-3 px-6 sm:px-7 py-3 rounded-full bg-white hover:bg-neutral-900 text-neutral-900 hover:text-white border border-neutral-900 font-sans font-bold text-xs sm:text-sm transition-all shadow-xs hover:shadow-md cursor-pointer active:scale-95"
        >
          <span>Customize Your Hamper</span>
          <span className="w-6 h-6 rounded-full bg-neutral-900 group-hover:bg-white text-white group-hover:text-neutral-900 flex items-center justify-center transform group-hover:translate-x-0.5 transition-all">
            <ArrowRight className="w-3.5 h-3.5 stroke-[2.5]" />
          </span>
        </button>

        <Link
          href="/occasions"
          className="text-xs font-bold tracking-wider text-neutral-600 hover:text-neutral-900 transition-colors uppercase inline-flex items-center space-x-1 py-1"
        >
          <span>View All Occasion Hampers</span>
          <ArrowRight className="w-3.5 h-3.5 ml-0.5" />
        </Link>

        <a
          href="#corporate-bulk"
          className="text-xs font-bold tracking-wider text-neutral-500 hover:text-neutral-900 transition-colors uppercase inline-flex items-center space-x-1 py-1"
        >
          <span>Or Corporate Bulk Orders</span>
          <ArrowRight className="w-3.5 h-3.5 ml-0.5" />
        </a>
      </div>

    </section>
  );
}
